import React, { useCallback, useEffect, useMemo, useState } from "react";
import {
  Box, Stack, Typography, Skeleton, Card, Grid, Divider,
  ToggleButton, ToggleButtonGroup,
} from "@mui/material";
import {
  AreaChart, Area, ResponsiveContainer, Tooltip as RTooltip,
  XAxis, YAxis, CartesianGrid,
} from "recharts";
import axios from "axios";
import LabOrdersList from "./LabOrdersList";
import { getProviderMeta } from "../../../utils/labProviders";

const BASE = "https://api.medstown.com";

// ── Design tokens ───────────────────────────────────────────────────────────
const T = {
  bg:        "#f8fafc",
  surface:   "#ffffff",
  border:    "#e5e7eb",
  borderSoft:"#f1f5f9",
  ink:       "#0f172a",
  muted:     "#64748b",
  subtle:    "#94a3b8",
  accent:    "#4f46e5",
  accentSoft:"#eef2ff",
  green:     "#059669",
  greenSoft: "#ecfdf5",
  amber:     "#d97706",
  amberSoft: "#fffbeb",
  red:       "#dc2626",
  redSoft:   "#fef2f2",
  violet:    "#7c3aed",
  violetSoft:"#f5f3ff",
};

const STATUS_TONE = {
  Scheduled:             T.accent,
  Confirmed:             "#0284c7",
  Assigned:              "#0891b2",
  SampleCollected:       T.violet,
  ReportGenerated:       T.green,
  Completed:             T.green,
  Rescheduled:           T.amber,
  CancellationRequested: T.amber,
  Cancelled:             T.red,
};

const inr = (n) =>
  "₹" + Number(n || 0).toLocaleString("en-IN", { maximumFractionDigits: 0 });

const inrShort = (n) => {
  const v = Number(n || 0);
  if (v >= 1e7) return `₹${(v / 1e7).toFixed(1)}Cr`;
  if (v >= 1e5) return `₹${(v / 1e5).toFixed(1)}L`;
  if (v >= 1e3) return `₹${(v / 1e3).toFixed(1)}k`;
  return `₹${v}`;
};

// ── Shared primitives ───────────────────────────────────────────────────────
function Surface({ children, sx, ...rest }) {
  return (
    <Card elevation={0} sx={{
      bgcolor: T.surface,
      border: `1px solid ${T.border}`,
      borderRadius: 3,
      boxShadow: "none",
      ...sx,
    }} {...rest}>
      {children}
    </Card>
  );
}

function SectionTitle({ children, action }) {
  return (
    <Stack direction="row" alignItems="center" justifyContent="space-between" sx={{ mb: 1.2 }}>
      <Typography sx={{
        fontSize: "0.68rem",
        fontWeight: 700,
        color: T.muted,
        textTransform: "uppercase",
        letterSpacing: "0.08em",
      }}>
        {children}
      </Typography>
      {action}
    </Stack>
  );
}

function Kpi({ label, value, sub, loading, tone = T.ink }) {
  return (
    <Box sx={{ px: 2, py: 1.6 }}>
      <Typography sx={{
        fontSize: "0.65rem",
        fontWeight: 600,
        color: T.muted,
        textTransform: "uppercase",
        letterSpacing: "0.07em",
        mb: 0.4,
      }}>
        {label}
      </Typography>
      {loading ? (
        <Skeleton width={70} height={28} />
      ) : (
        <Typography sx={{
          fontSize: "1.45rem",
          fontWeight: 700,
          color: tone,
          lineHeight: 1.1,
          fontVariantNumeric: "tabular-nums",
          letterSpacing: "-0.01em",
        }}>
          {value}
        </Typography>
      )}
      {sub && (
        <Typography sx={{
          fontSize: "0.7rem",
          color: T.subtle,
          mt: 0.3,
          fontVariantNumeric: "tabular-nums",
        }}>
          {sub}
        </Typography>
      )}
    </Box>
  );
}

// ── Trend chart ─────────────────────────────────────────────────────────────
function TrendChart({ data, loading, range, onRangeChange }) {
  const chartData = useMemo(() => {
    if (!data?.length) return [];
    const days = range === 7 ? 7 : 30;
    return data.slice(-days).map((d) => ({
      date: d._id,
      count: d.count,
      revenue: d.revenue,
      label: (() => {
        const dt = new Date(d._id);
        return dt.toLocaleDateString("en-IN", { day: "2-digit", month: "short" });
      })(),
    }));
  }, [data, range]);

  return (
    <Surface sx={{ p: 2.2, height: "100%" }}>
      <SectionTitle action={
        <ToggleButtonGroup
          value={range} exclusive size="small"
          onChange={(_, v) => v && onRangeChange(v)}
          sx={{
            "& .MuiToggleButton-root": {
              border: `1px solid ${T.border}`,
              color: T.muted,
              fontSize: "0.68rem",
              fontWeight: 600,
              textTransform: "none",
              px: 1.2, py: 0.2,
              "&.Mui-selected": {
                bgcolor: T.ink,
                color: "#fff",
                "&:hover": { bgcolor: T.ink },
              },
            },
          }}
        >
          <ToggleButton value={7}>7D</ToggleButton>
          <ToggleButton value={30}>30D</ToggleButton>
        </ToggleButtonGroup>
      }>
        Order volume
      </SectionTitle>

      <Box sx={{ height: 220, mx: -1 }}>
        {loading ? (
          <Skeleton variant="rectangular" height="100%" sx={{ borderRadius: 2 }} />
        ) : !chartData.length ? (
          <Stack alignItems="center" justifyContent="center" sx={{ height: "100%" }}>
            <Typography variant="caption" sx={{ color: T.subtle }}>No data yet</Typography>
          </Stack>
        ) : (
          <ResponsiveContainer width="100%" height="100%">
            <AreaChart data={chartData} margin={{ top: 8, right: 12, left: 0, bottom: 0 }}>
              <defs>
                <linearGradient id="volFill" x1="0" y1="0" x2="0" y2="1">
                  <stop offset="0%"   stopColor={T.accent} stopOpacity={0.28} />
                  <stop offset="100%" stopColor={T.accent} stopOpacity={0} />
                </linearGradient>
              </defs>
              <CartesianGrid stroke={T.borderSoft} strokeDasharray="0" vertical={false} />
              <XAxis
                dataKey="label"
                tick={{ fontSize: 10, fill: T.subtle }}
                axisLine={{ stroke: T.border }}
                tickLine={false}
                interval="preserveStartEnd"
              />
              <YAxis
                tick={{ fontSize: 10, fill: T.subtle }}
                axisLine={false}
                tickLine={false}
                width={28}
              />
              <RTooltip
                contentStyle={{
                  borderRadius: 8,
                  border: `1px solid ${T.border}`,
                  fontSize: "0.72rem",
                  boxShadow: "0 8px 24px rgba(15,23,42,0.08)",
                }}
                labelStyle={{ color: T.ink, fontWeight: 700 }}
                formatter={(v, k) => k === "revenue" ? inr(v) : [v, "Orders"]}
              />
              <Area
                type="monotone"
                dataKey="count"
                stroke={T.accent}
                strokeWidth={2}
                fill="url(#volFill)"
                activeDot={{ r: 4, stroke: "#fff", strokeWidth: 2 }}
              />
            </AreaChart>
          </ResponsiveContainer>
        )}
      </Box>
    </Surface>
  );
}

// ── Status donut (pure CSS conic-gradient) ──────────────────────────────────
function StatusDonut({ data, loading }) {
  const total = (data || []).reduce((s, r) => s + r.count, 0);
  const sorted = (data || []).slice().sort((a, b) => b.count - a.count);

  const segments = useMemo(() => {
    if (!total) return [];
    let acc = 0;
    return sorted.map((r) => {
      const pct = (r.count / total) * 100;
      const tone = STATUS_TONE[r._id] || T.muted;
      const seg = { key: r._id || "unknown", tone, pct, count: r.count, start: acc, end: acc + pct };
      acc += pct;
      return seg;
    });
  }, [sorted, total]);

  const gradient = segments.length
    ? `conic-gradient(${segments
        .map((s) => `${s.tone} ${s.start}% ${s.end}%`)
        .join(", ")})`
    : `conic-gradient(${T.borderSoft} 0% 100%)`;

  return (
    <Surface sx={{ p: 2.2, height: "100%" }}>
      <SectionTitle>Status mix</SectionTitle>
      {loading ? (
        <Skeleton variant="circular" width={120} height={120} />
      ) : !total ? (
        <Typography variant="caption" sx={{ color: T.subtle }}>No orders</Typography>
      ) : (
        <Stack direction="row" spacing={2.2} alignItems="center">
          <Box sx={{
            width: 112, height: 112, borderRadius: "50%",
            background: gradient, position: "relative", flexShrink: 0,
          }}>
            <Box sx={{
              position: "absolute", inset: 14, borderRadius: "50%",
              bgcolor: T.surface,
              display: "flex", flexDirection: "column",
              alignItems: "center", justifyContent: "center",
              border: `1px solid ${T.borderSoft}`,
            }}>
              <Typography sx={{ fontSize: "1.3rem", fontWeight: 700, color: T.ink, lineHeight: 1, fontVariantNumeric: "tabular-nums" }}>
                {total}
              </Typography>
              <Typography sx={{ fontSize: "0.6rem", color: T.muted, textTransform: "uppercase", letterSpacing: "0.05em", mt: 0.2 }}>
                Total
              </Typography>
            </Box>
          </Box>

          <Stack spacing={0.6} sx={{ flex: 1, minWidth: 0 }}>
            {segments.slice(0, 5).map((s) => (
              <Stack key={s.key} direction="row" alignItems="center" spacing={0.8}>
                <Box sx={{
                  width: 8, height: 8, borderRadius: "50%",
                  bgcolor: s.tone, flexShrink: 0,
                }} />
                <Typography sx={{ fontSize: "0.72rem", color: T.ink, flex: 1, minWidth: 0 }} noWrap>
                  {s.key}
                </Typography>
                <Typography sx={{
                  fontSize: "0.72rem", color: T.muted, fontVariantNumeric: "tabular-nums",
                }}>
                  {s.count} · {s.pct.toFixed(0)}%
                </Typography>
              </Stack>
            ))}
          </Stack>
        </Stack>
      )}
    </Surface>
  );
}

// ── Provider + pincodes stack ───────────────────────────────────────────────
function SplitCard({ analytics, loading }) {
  const byProvider = analytics?.byProvider || [];
  const pincodes   = (analytics?.topPincodes || []).filter((p) => p._id).slice(0, 5);

  return (
    <Surface sx={{ p: 2.2, height: "100%" }}>
      <SectionTitle>Partners &amp; regions</SectionTitle>

      <Stack spacing={1.2}>
        {loading ? (
          <>
            <Skeleton height={36} sx={{ borderRadius: 2 }} />
            <Skeleton height={36} sx={{ borderRadius: 2 }} />
          </>
        ) : byProvider.length === 0 ? (
          <Typography variant="caption" sx={{ color: T.subtle }}>No partner data</Typography>
        ) : (
          byProvider.map((row) => {
            const m = getProviderMeta(row._id);
            return (
              <Stack key={row._id} direction="row" alignItems="center" justifyContent="space-between">
                <Stack direction="row" alignItems="center" spacing={1}>
                  <Box sx={{ width: 6, height: 24, borderRadius: 1, bgcolor: m.tone }} />
                  <Box>
                    <Typography sx={{ fontSize: "0.78rem", fontWeight: 600, color: T.ink, lineHeight: 1.2 }}>
                      {m.label}
                    </Typography>
                    <Typography sx={{ fontSize: "0.66rem", color: T.muted }}>
                      {row.count} orders
                    </Typography>
                  </Box>
                </Stack>
                <Typography sx={{
                  fontSize: "0.82rem", fontWeight: 700, color: T.ink,
                  fontVariantNumeric: "tabular-nums",
                }}>
                  {inrShort(row.revenue)}
                </Typography>
              </Stack>
            );
          })
        )}
      </Stack>

      {pincodes.length > 0 && (
        <>
          <Divider sx={{ my: 1.5, borderColor: T.borderSoft }} />
          <Typography sx={{
            fontSize: "0.62rem", fontWeight: 700, color: T.muted,
            textTransform: "uppercase", letterSpacing: "0.07em", mb: 0.6,
          }}>
            Top pincodes
          </Typography>
          <Stack spacing={0.5}>
            {pincodes.map((p) => (
              <Stack key={p._id} direction="row" justifyContent="space-between" alignItems="center">
                <Typography sx={{ fontSize: "0.74rem", color: T.ink, fontFamily: "ui-monospace, SFMono-Regular, Menlo, monospace" }}>
                  {p._id}
                </Typography>
                <Typography sx={{ fontSize: "0.72rem", color: T.muted, fontVariantNumeric: "tabular-nums" }}>
                  {p.count}
                </Typography>
              </Stack>
            ))}
          </Stack>
        </>
      )}
    </Surface>
  );
}

// ── Main ────────────────────────────────────────────────────────────────────
export default function LabOrders() {
  const [analytics, setAnalytics] = useState(null);
  const [loading, setLoading]     = useState(false);
  const [range, setRange]         = useState(30);

  const fetchAnalytics = useCallback(async () => {
    setLoading(true);
    try {
      const { data } = await axios.get(`${BASE}/admin/lab/orders/analytics`);
      setAnalytics(data.analytics || null);
    } catch {
      /* silent */
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => { fetchAnalytics(); }, [fetchAnalytics]);

  return (
    <Box sx={{
      mt: 2,
      "& *::-webkit-scrollbar": { width: 8, height: 8 },
      "& *::-webkit-scrollbar-thumb": { background: T.border, borderRadius: 4 },
    }}>
      {/* Page header */}
      <Stack direction="row" alignItems="flex-end" justifyContent="space-between" sx={{ mb: 2 }}>
        <Box>
          <Typography sx={{
            fontSize: "1.35rem", fontWeight: 700, color: T.ink,
            letterSpacing: "-0.02em", lineHeight: 1.1,
          }}>
            Lab Orders
          </Typography>
          <Typography sx={{ fontSize: "0.78rem", color: T.muted, mt: 0.3 }}>
            Monitor bookings, revenue and fulfilment across partner labs.
          </Typography>
        </Box>
      </Stack>

      {/* KPI strip */}
      <Surface sx={{ mb: 1.5, overflow: "hidden" }}>
        <Grid container sx={{
          "& > .MuiGrid-item": {
            borderRight: `1px solid ${T.borderSoft}`,
            "&:last-of-type": { borderRight: 0 },
          },
          "& > .MuiGrid-item:nth-of-type(n+4)": {
            borderTop: { xs: `1px solid ${T.borderSoft}`, md: 0 },
          },
          "& > .MuiGrid-item:nth-of-type(3)": {
            borderRight: { xs: 0, md: `1px solid ${T.borderSoft}` },
          },
        }}>
          <Grid item xs={6} md={2.4}>
            <Kpi
              label="Total orders"
              value={loading ? "—" : (analytics?.total ?? 0).toLocaleString("en-IN")}
              sub={analytics ? `${inrShort(analytics.revenue)} gross` : ""}
              loading={loading}
            />
          </Grid>
          <Grid item xs={6} md={2.4}>
            <Kpi
              label="Paid"
              value={loading ? "—" : (analytics?.paid ?? 0).toLocaleString("en-IN")}
              sub={analytics ? inrShort(analytics.paidRevenue) : ""}
              tone={T.green}
              loading={loading}
            />
          </Grid>
          <Grid item xs={6} md={2.4}>
            <Kpi
              label="Pending payment"
              value={loading ? "—" : (analytics?.pending ?? 0).toLocaleString("en-IN")}
              tone={T.amber}
              loading={loading}
            />
          </Grid>
          <Grid item xs={6} md={2.4}>
            <Kpi
              label="Today"
              value={loading ? "—" : (analytics?.today?.count ?? 0).toLocaleString("en-IN")}
              sub={analytics ? inrShort(analytics.today?.revenue) : ""}
              loading={loading}
            />
          </Grid>
          <Grid item xs={12} md={2.4}>
            <Kpi
              label={`Last ${range} days`}
              value={
                loading
                  ? "—"
                  : range === 7
                    ? (analytics?.last7?.count ?? 0).toLocaleString("en-IN")
                    : (analytics?.last30?.count ?? 0).toLocaleString("en-IN")
              }
              sub={
                analytics
                  ? inrShort(range === 7 ? analytics.last7?.revenue : analytics.last30?.revenue)
                  : ""
              }
              tone={T.accent}
              loading={loading}
            />
          </Grid>
        </Grid>
      </Surface>

      {/* Analytics row */}
      <Grid container spacing={1.5} sx={{ mb: 2 }}>
        <Grid item xs={12} md={6}>
          <TrendChart
            data={analytics?.dailyTrend}
            loading={loading}
            range={range}
            onRangeChange={setRange}
          />
        </Grid>
        <Grid item xs={12} md={3}>
          <StatusDonut data={analytics?.byStatus} loading={loading} />
        </Grid>
        <Grid item xs={12} md={3}>
          <SplitCard analytics={analytics} loading={loading} />
        </Grid>
      </Grid>

      <LabOrdersList onMutate={fetchAnalytics} />
    </Box>
  );
}
